import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFilter } from '@fortawesome/free-solid-svg-icons';
import useComponentState from '../../utils/useComponentState';
import { clientGraphQL, getRedemptionMerchantCSV } from '../../api/Client';
import Pagination from '../../components/pagination/Pagination.component';
import FilterOverlay from '../../components/FilterOverlay/FilterOverlay.component';
import './ClientRedemptionMerchantSummary.scss';

const PAGE_SIZE = 10;
const DEFAULT_SEARCH_BY = 'merchant.name';
const DEFAULT_SORT_BY = 'redemptionCount';
const DEFAULT_SORT_DIR = 'DESC';

const SEARCH_FIELD_OPTIONS = [
    { label: 'Merchant Name', value: 'merchant.name' },
    { label: 'Merchant Category', value: 'merchant.category' },
];

const SORT_BY_OPTIONS = [
    { label: 'Redemption Count', value: 'redemptionCount' },
    { label: 'Merchant Name', value: 'merchant.name' },
    { label: 'Category', value: 'merchant.category' },
];

const SORT_DIR_OPTIONS = [
    { label: 'Descending', value: 'DESC' },
    { label: 'Ascending', value: 'ASC' },
];

const BASE_FILTERS = [
    '{ key: "status", value: "COMPLETE" }',
];

const CLIENT_VOUCHER_TYPE_MAP = {
    AIA: 'AIA_REWARDS',
    NDB: 'NDB_REWARDS',
};

const ClientRedemptionMerchantSummary = (props) => {
    const [state, setState] = useComponentState({
        list: [],
        pageNo: 0,
        totalPageCount: 0,
        loading: false,
        downloadingCSV: false,
        error: null,
        filters: {
            searchBy: DEFAULT_SEARCH_BY,
            search: '',
            sortBy: DEFAULT_SORT_BY,
            sortDir: DEFAULT_SORT_DIR,
        },
        appliedFilters: {
            searchBy: DEFAULT_SEARCH_BY,
            search: '',
            sortBy: DEFAULT_SORT_BY,
            sortDir: DEFAULT_SORT_DIR,
        },
        isFilterOverlayOpen: false,
    });

    const { auth, user } = props;


    const escapeGraphqlString = (value) =>
        String(value).replace(/\\/g, '\\\\').replace(/"/g, '\\"');

    const buildFiltersQuery = (activeFilters, voucherType) => {
        const fragments = [...BASE_FILTERS];
        const searchText = (activeFilters.search || '').trim();

        if (voucherType) {
            fragments.push(`{ key: "voucher.type", value: "${escapeGraphqlString(voucherType)}" }`);
        }

        if (searchText) {
            fragments.push(
                `{ key: "${escapeGraphqlString(activeFilters.searchBy || DEFAULT_SEARCH_BY)}", value: "${escapeGraphqlString(searchText)}" }`
            );
        }

        return fragments.join(',\n              ');
    };

    const mapClientToVoucherType = (clientName) => {
        if (!clientName) {
            return null;
        }

        const normalized = String(clientName).trim().toUpperCase();
        return CLIENT_VOUCHER_TYPE_MAP[normalized] || normalized;
    };

    const getVoucherTypeFromUserDetails = () => {
        const authClientName = auth?.details?.clientName;
        const resolvedClientName = authClientName;

        if (!resolvedClientName) {
            return null;
        }

        return mapClientToVoucherType(resolvedClientName);
    };

    const fetchMerchants = async (page = 0, activeFilters = state.appliedFilters) => {
        setState({ loading: true, error: null });

        const voucherType = getVoucherTypeFromUserDetails();
        const filtersQuery = buildFiltersQuery(activeFilters, voucherType);
        const sortBy = activeFilters.sortBy || DEFAULT_SORT_BY;
        const sortDir = activeFilters.sortDir || DEFAULT_SORT_DIR;

        const query = `
			{
				merchantListByRedemption(
					filters: {
						filters: [
							${filtersQuery}
						]
					},
					page: ${page},
					size: ${PAGE_SIZE},
					sortBy: "${escapeGraphqlString(sortBy)}",
					sortDir: "${escapeGraphqlString(sortDir)}"
				) {
					pageNo
					totalPageCount
					list {
						merchant {
							name
							category
							thumbnail
							website

						}
						redemptionCount
					}
				}
			}
		`;

        try {
            const response = await clientGraphQL(query, auth.token);
            const data = response?.data?.merchantListByRedemption || {};

            setState({
                list: data.list || [],
                pageNo: data.pageNo || 0,
                totalPageCount: data.totalPageCount || 0,
                loading: false,
            });
        } catch (err) {
            setState({
                loading: false,
                error: err?.message || 'Failed to load merchant redemption summary',
            });
        }
    };

    useEffect(() => {
        if (!auth?.token) {
            return;
        }

        fetchMerchants(0, state.appliedFilters);
    }, [auth?.token, auth?.details?.clientName, user?.currentUser?.clientName]);

    const handlePageChange = (selectedPage) => {
        fetchMerchants(selectedPage, state.appliedFilters);
    };

    const handleFilterChange = (key, value) => {
        setState({
            filters: {
                ...state.filters,
                [key]: value,
            },
        });
    };

    const applyFilters = () => {
        const nextAppliedFilters = {
            ...state.filters,
        };

        setState({
            appliedFilters: nextAppliedFilters,
            isFilterOverlayOpen: false,
        });

        fetchMerchants(0, nextAppliedFilters);
    };

    const clearFilters = () => {
        const resetFilters = {
            searchBy: DEFAULT_SEARCH_BY,
            search: '',
            sortBy: DEFAULT_SORT_BY,
            sortDir: DEFAULT_SORT_DIR,
        };

        setState({
            filters: resetFilters,
            appliedFilters: resetFilters,
            isFilterOverlayOpen: false,
        });

        fetchMerchants(0, resetFilters);
    };

    const toggleFilterOverlay = (isOpen) => {
        setState({ isFilterOverlayOpen: isOpen });
    };

    const downloadCSV = async () => {
        setState({ downloadingCSV: true, error: null });
        const voucherType = getVoucherTypeFromUserDetails();

        const filters = [...BASE_FILTERS.map(f => eval(`(${f})`))];

        if (voucherType) {
            filters.push({ key: "voucher.type", value: voucherType });
        }

        const searchText = (state.appliedFilters.search || '').trim();
        if (searchText) {
            filters.push({
                key: state.appliedFilters.searchBy || DEFAULT_SEARCH_BY,
                value: searchText
            });
        }

        try {
            const sortBy = state.appliedFilters.sortBy || DEFAULT_SORT_BY;
            const sortDir = state.appliedFilters.sortDir || DEFAULT_SORT_DIR;
            const response = await getRedemptionMerchantCSV({ filters }, auth.token, sortBy, sortDir);
            const blob = new Blob([response], { type: 'text/csv' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'merchant_redemption_summary.csv');
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } catch (err) {
            setState({ downloadingCSV: false, error: err?.message || 'Failed to download CSV' });
            return;
        } finally {
            setState({ downloadingCSV: false });
        }
    };

    const { list, pageNo, totalPageCount, loading, downloadingCSV, error } = state;

    const hasAppliedFilters = Boolean(
        state.appliedFilters.search ||
        state.appliedFilters.searchBy !== DEFAULT_SEARCH_BY ||
        state.appliedFilters.sortBy !== DEFAULT_SORT_BY ||
        state.appliedFilters.sortDir !== DEFAULT_SORT_DIR
    );

    return (
        <div className="merchant_summary_root">
            <div className="page_title_row">
                <h2>Merchant Redemption Summary</h2>
                <div className="page_title_line" />
            </div>

            <div className="filters_toolbar">
                <button
                    type="button"
                    className="open_filter_btn"
                    onClick={() => toggleFilterOverlay(true)}
                    aria-label="Open filters"
                    title="Open filters"
                >
                    <FontAwesomeIcon icon={faFilter} className="open_filter_icon" />
                </button>
                <button
                    type="button"
                    className="download_csv_btn"
                    onClick={downloadCSV}
                    disabled={downloadingCSV}
                    aria-label="Download CSV"
                    title="Download CSV"
                >
                    {downloadingCSV ? 'Downloading...' : 'Download CSV'}
                </button>

                {hasAppliedFilters && (
                    <button
                        type="button"
                        className="quick_reset_btn"
                        onClick={clearFilters}
                        aria-label="Reset filters"
                        title="Reset filters"
                    >
                        Reset
                    </button>
                )}
            </div>

            <FilterOverlay
                isOpen={state.isFilterOverlayOpen}
                title="Merchant Summary Filters"
                filters={state.filters}
                showSearch
                showDateRange={false}
                searchPlaceholder="Search merchant by selected field"
                dropdownDetails={[
                    {
                        key: 'searchBy',
                        label: 'Search By',
                        options: SEARCH_FIELD_OPTIONS,
                        section: 'filter',
                    },
                    {
                        key: 'sortBy',
                        label: 'Sort By',
                        options: SORT_BY_OPTIONS,
                        section: 'sort',
                    },
                    {
                        key: 'sortDir',
                        label: 'Sort Direction',
                        options: SORT_DIR_OPTIONS,
                        section: 'sort',
                    },
                ]}
                onChange={handleFilterChange}
                onApply={applyFilters}
                onReset={clearFilters}
                onClose={() => toggleFilterOverlay(false)}
            />

            {loading ? (
                <div className="state_message">Loading...</div>
            ) : error ? (
                <div className="state_message state_message_error">{error}</div>
            ) : (
                <>
                    <div className="merchant_summary_table_container">
                        <div className="merchant_summary_header">
                            <span>Image</span>
                            <span>Merchant Name</span>
                            <span>Category</span>
                            <span>Redemption Count</span>
                            <span>Website</span>
                        </div>

                        <div className="merchant_summary_list">
                            {list.length === 0 ? (
                                <div className="state_message">No merchant redemptions found for current filters.</div>
                            ) : (
                                list.map((item, index) => (
                                    <div key={`${item?.merchant?.name || 'merchant'}-${index}`} className="merchant_summary_row">
                                        <span>
                                            {item?.merchant?.thumbnail ? (
                                                <img src={item.merchant.thumbnail} alt="Merchant" className="merchant_media_thumb" />
                                            ) : (
                                                '-'
                                            )}
                                        </span>
                                        <span className="merchant_text_truncate">{item?.merchant?.name || '-'}</span>
                                        <span className="merchant_text_truncate">{item?.merchant?.category || '-'}</span>
                                        <span>{Number(item?.redemptionCount || 0).toLocaleString()}</span>
                                        <span>
                                            {item?.merchant?.website ? (
                                                <a
                                                    href={item.merchant.website}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="merchant_link"
                                                >
                                                    Visit
                                                </a>
                                            ) : (
                                                '-'
                                            )}
                                        </span>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>

                    <div className="pagination_toolbar">
                        <Pagination
                            pageCount={totalPageCount}
                            currentPage={pageNo}
                            onChange={handlePageChange}
                        />
                    </div>
                </>
            )}
        </div>
    );
};

const mapStateToProps = (state) => ({
    auth: state.auth.auth,
    user: state.user,
});

export default connect(mapStateToProps)(ClientRedemptionMerchantSummary);
